import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useState } from "react";
import Dropzone from "react-dropzone";
import { useSpinDelay } from "spin-delay";
import ArticleList from "./ArticleList";
import ExistingArticlesList from "./ExistingArticlesList";
import UpdatedActiveArticlesList from "./UpdatedActiveArticlesList";
import {
  BaseUpdate,
  BaseValidateResponse,
  DropzoneWrapper,
  EditableBaseUpdate,
  ItemTitle,
  LoadingWrapper,
  PageWrapper,
  SaveResponse,
} from "../common/visma";
import PluralText, { PluralData } from "../common/PluralText";

export interface CompanyType {
  companyTypeId: number;
  name: string;
}

export interface Props {
  urls: {
    validate: string;
    save: string;
  };
  companyTypes: CompanyType[];
}

export interface VismaArticle {
  articleNo: string;
  articleGroup: string | null;
  isActive: boolean | null;
  name: string | null;
  unit: string | null;
  price: number | null;
}

export enum ValueType {
  None = 0,
  ArticleNo = 1 << 0,
  ArticleGroup = 1 << 1,
  IsActive = 1 << 2,
  Name = 1 << 3,
  Unit = 1 << 4,
  Price = 1 << 5,
}

export interface ArticleDelta {
  new: VismaArticle[];
  updates: Update[];
  updatedActiveStates: UpdatedActiveState[];
}

export interface UpdatedActiveState {
  articleNo: string;
  name: string;
  active: boolean;
}

export type Update = BaseUpdate<VismaArticle, ValueType>;
export type EditableUpdate = EditableBaseUpdate<VismaArticle, ValueType>;
export type ValidateResponse = BaseValidateResponse<ArticleDelta>;

enum PageState {
  WaitingForFileInput,
  ValidatingFile,
  WaitingForSaveInput,
  SavingFile,
  Saved,
}

const ImportArticlesPage = ({ urls, companyTypes }: Props) => {
  const [pageState, setPageState] = useState(PageState.WaitingForFileInput);
  const [validateResponse, setValidateResponse] = useState(
    null as ValidateResponse | null
  );
  const [saveErrorMessage, setSaveErrorMessage] = useState(
    null as string | null
  );
  const [articleUpdates, setArticleUpdates] = useState(
    null as EditableUpdate[] | null
  );
  const [newArticleCompanyTypes, setNewArticleCompanyTypes] = useState(
    new Map<string, CompanyType>()
  );
  const showValidationLoading = useSpinDelay(
    pageState === PageState.ValidatingFile
  );
  const showSaveLoading = useSpinDelay(pageState === PageState.SavingFile);

  const onFileSelected = async (files) => {
    let file = files[0];

    if (file == null) {
      return;
    }

    setPageState(PageState.ValidatingFile);
    const data = new FormData();
    data.append("file", file);

    const response = await fetch(urls.validate, {
      method: "POST",
      body: data,
    });

    const responseData = (await response.json()) as ValidateResponse;
    setValidateResponse(responseData);

    if (responseData.success) {
      const newArticleUpdates = [] as EditableUpdate[];
      for (let i = 0; i < responseData.delta.updates.length; ++i) {
        let update = responseData.delta.updates[i];

        newArticleUpdates.push({
          ...update,
          overwrites: update.defaultOverwrites ?? update.conflicts,
        });
      }

      const newArticleCompanyTypes = new Map<string, CompanyType>();
      for (let i = 0; i < responseData.delta.new.length; ++i) {
        let article = responseData.delta.new[i];
        newArticleCompanyTypes.set(article.articleNo!, companyTypes[0]);
      }

      setArticleUpdates(newArticleUpdates);
      setNewArticleCompanyTypes(newArticleCompanyTypes);
      setPageState(PageState.WaitingForSaveInput);
    } else {
      setPageState(PageState.WaitingForFileInput);
    }
  };

  const saveImport = async () => {
    if (articleUpdates == null) {
      return;
    }

    setPageState(PageState.SavingFile);

    const overwriteOptions = {
      overwrites: Object.fromEntries(
        articleUpdates.map((upd) => {
          let overwrites = upd.changes;
          overwrites &= ~upd.conflicts; // remove conflicts from overwrites
          overwrites |= upd.overwrites; // add overwrites set by user
          return [upd.new.articleNo, overwrites];
        })
      ),
      companyTypes: Object.fromEntries(
        [...newArticleCompanyTypes.entries()].map(([k, v]) => [
          k,
          v.companyTypeId,
        ])
      ),
    };

    const response = await fetch(
      urls.save + `?importId=${validateResponse?.saveImportId}`,
      {
        method: "POST",
        body: JSON.stringify(overwriteOptions),
        headers: [["Content-Type", "application/json"]],
      }
    );

    let responseData = (await response.json()) as SaveResponse;
    setSaveErrorMessage(responseData.message);

    if (responseData.success) {
      setPageState(PageState.Saved);
      setTimeout(() => {
        document.location.href = responseData.redirectUrl;
      }, 3000);
    } else {
      setPageState(PageState.WaitingForSaveInput);
    }
  };

  function onSetFieldOverwrite(
    article: EditableUpdate,
    newOverwriteFlags: ValueType
  ) {
    setArticleUpdates(
      articleUpdates?.map((mapArticle) => {
        if (mapArticle.new.articleNo === article.new.articleNo) {
          return {
            ...mapArticle,
            overwrites: newOverwriteFlags,
          };
        }

        return mapArticle;
      }) ?? null
    );
  }

  function onNewArticleCompanyTypeChanged(
    article: VismaArticle,
    companyType: CompanyType
  ) {
    setNewArticleCompanyTypes(
      new Map(newArticleCompanyTypes).set(article.articleNo, companyType)
    );
  }

  const renderPageState = (): React.ReactNode => {
    switch (pageState) {
      case PageState.WaitingForFileInput:
      case PageState.ValidatingFile: {
        return (
          <div>
            <Dropzone onDrop={onFileSelected} multiple={false}>
              {({ getRootProps, getInputProps }) => (
                <DropzoneWrapper>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <span>
                      Klicka eller släpp en fil här för att importera från Visma
                      Administration
                    </span>
                  </div>
                </DropzoneWrapper>
              )}
            </Dropzone>
            {validateResponse?.message && (
              <span className="error-message">{validateResponse.message}</span>
            )}
          </div>
        );
      }
      case PageState.WaitingForSaveInput:
      case PageState.SavingFile: {
        const numNewArticles = validateResponse?.delta.new.length;
        const numUpdatedArticles = validateResponse?.delta.updates.length;
        const numUpdatedActiveStateArticles =
          validateResponse?.delta.updatedActiveStates.length;
        const articlePlural: PluralData = {
          singular: "artikel",
          plural: "artiklar",
        };
        return (
          <div>
            <ItemTitle>Nya artiklar</ItemTitle>
            <ArticleList
              articles={validateResponse!.delta.new}
              companyTypes={companyTypes}
              companyTypeMap={newArticleCompanyTypes}
              onCompanyTypeChanged={onNewArticleCompanyTypeChanged}
            />
            <ItemTitle>Artiklar med nya värden</ItemTitle>
            <ExistingArticlesList
              articles={articleUpdates!}
              onSetFieldOverwrite={onSetFieldOverwrite}
            />
            <ItemTitle>Artiklar med ny aktiv status</ItemTitle>
            <UpdatedActiveArticlesList
              articles={validateResponse?.delta.updatedActiveStates ?? []}
            />
            <ItemTitle>Sammanfattning</ItemTitle>
            {!numNewArticles &&
            !numUpdatedArticles &&
            !numUpdatedActiveStateArticles ? (
              <p>Hittade ingen ny data att importera.</p>
            ) : null}
            {numNewArticles ||
            numUpdatedArticles ||
            numUpdatedActiveStateArticles ? (
              <>
                <ul>
                  <li>
                    {numNewArticles} nya{" "}
                    <PluralText count={numNewArticles} data={articlePlural} />
                  </li>
                  <li>
                    {numUpdatedArticles} existerande{" "}
                    <PluralText
                      count={numUpdatedArticles}
                      data={articlePlural}
                    />{" "}
                    med nya värden
                  </li>
                  <li>
                    {numUpdatedActiveStateArticles}{" "}
                    <PluralText
                      count={numUpdatedActiveStateArticles}
                      data={articlePlural}
                    />{" "}
                    med ny aktiv status
                  </li>
                </ul>
                <button
                  className="button primary"
                  onClick={saveImport}
                  disabled={pageState == PageState.SavingFile}
                >
                  Importera
                </button>
                {saveErrorMessage?.length && (
                  <span className="error-message">{saveErrorMessage}</span>
                )}
              </>
            ) : null}
          </div>
        );
      }
      case PageState.Saved: {
        return (
          <LoadingWrapper>
            Importeringen slutfördes utan problem. Du skickas tillbaka till
            artikellistan om 3 sekunder.
          </LoadingWrapper>
        );
      }
      default:
        throw new Error(`Unhandled PageState: ${pageState}`);
    }
  };

  return (
    <PageWrapper>
      {renderPageState()}

      {showValidationLoading && (
        <LoadingWrapper>
          <FontAwesomeIcon icon={faSpinner} spin={true} size="4x" />
          Vänta medan filens innehåll kontrolleras och jämförs med existerande
          värden...
        </LoadingWrapper>
      )}
      {showSaveLoading && (
        <LoadingWrapper>
          <FontAwesomeIcon icon={faSpinner} spin={true} size="4x" />
          Vänta medan artiklarna importeras...
        </LoadingWrapper>
      )}
    </PageWrapper>
  );
};

export default ImportArticlesPage;
